import jsPDF from "jspdf";
import { removeLogoCanvasBackground } from "./logo-background";
import { withQuotedCostSplit } from "@workspace/api-zod/quote-cost-split";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { COST_CATEGORIES, getCostCategories } from "@shared/schema";
import { ICON_NODES } from "./pdf-icon-data";
import { groupEquivalentLineItems, ITEM_SUB_GROUP_LABELS, type ItemSubGroupId, getCategorySection, SUB_GROUP_SECTION } from "@workspace/api-zod/work-type";
import { getEffectiveStage, getEffectiveSubGroup, QUOTE_PLACEMENT_STAGE_ORDER } from "./section-placement";
import { quoteHeadingLabel, type QuoteHeadingNames } from "@workspace/api-zod/quote-headings";
import { quoteCategoryLabel } from "./quote-heading-label";
import { readPdfTemplateSnapshot, type PdfTemplateSnapshot } from "@workspace/api-zod/pdf-template";
import { drawTemplateCover, drawTemplateClosing, hexRgb, tintRgb } from "./pdf-template-pages";
import { preparePdfTitleFont } from "./pdf-template-fonts";
import { pdfInteriorColour, pdfIconColour } from "./pdf-brand-colour";

interface QuoteLineItem {
  section?: string;
  scopeName?: string | null;
  category: string;
  name: string;
  description?: string | null;
  customerDescription?: string | null;
  inclusions?: string[];
  exclusions?: string[];
  quantity: string;
  unit: string;
  unitCost: string;
  labourCost?: string | null;
  materialCost?: string | null;
  plantCost?: string | null;
  subtotal: string;
  isOptional?: boolean;
  sortOrder?: number | null;
  displayStage?: string | null;
  displaySubGroup?: string | null;
}

export interface QuoteData {
  pdfTemplate?: PdfTemplateSnapshot | null;
  headingNames?: QuoteHeadingNames | null;
  quoteNumber: string;
  status: string | null;
  projectAddress: string | null;
  projectType: string | null;
  description: string | null;
  subtotal: string | null;
  overheadAmount: string | null;
  profitAmount: string | null;
  contingencyAmount: string | null;
  totalPrice: string | null;
  vatRate: string | null;
  vatAmount: string | null;
  grandTotal: string | null;
  includeVat: boolean | null;
  // When true, render a small Labour / Material / Plant breakdown line under
  // each item description. Mirrors the "Show breakdown to customer" toggle on
  // the quote detail page; defaults to false so existing PDFs are unchanged.
  showCostBreakdown?: boolean | null;
  // When false, skip the long-form description under each line item title
  // (Simple view). Defaults to true (Detailed view) when omitted.
  showItemDescriptions?: boolean | null;
  // When false, hide the Qty / Unit / Unit Cost columns from the customer
  // PDF and shared view. Subtotal column always remains. Defaults to true.
  showQuantityUnits?: boolean | null;
  // When false, skip the entire "Scope of Works" item table — the customer
  // PDF shows only the project description and grand total ("single total
  // price"). Defaults to true so existing PDFs are unchanged.
  showLineItems?: boolean | null;
  // When false, hide the Contingency / Overhead / Profit rows from the Quote
  // Summary card. The grand total is always correct; only the composition is
  // hidden. Defaults to true (show) so existing PDFs are unchanged.
  showMarkupBreakdown?: boolean | null;
  // When true, render one row per category + sub-group instead of the full
  // detailed item table. Mirrors the Summary Quote toggle in the builder UI.
  summaryMode?: boolean | null;
  validUntil: string | Date | null;
  assumptions: string | null;
  exclusions: string | null;
  termsConditions?: string | null;
  paymentTerms?: string | null;
  projectTimeline?: string | null;
  commencementNote?: string | null;
  createdAt: string | Date | null;
  confidenceLevel?: string | null;
  scopeLabel?: string | null;
  isMultiScope?: boolean | null;
  scopeNames?: string[] | null;
  lineItems: QuoteLineItem[];
  client: {
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    postcode?: string;
  } | null;
}

export interface CompanyInfo {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  vatNumber?: string;
  logoUrl?: string;
  showCompanyName?: boolean;
  logoSize?: "small" | "medium" | "large";
  market?: string;
}

export function shouldShowPdfSummaryPrices(quote: Pick<QuoteData, "showLineItems">): boolean {
  return quote.showLineItems !== false;
}

const UNIT_LABELS: Record<string, string> = {
  item: "Item", each: "Each", m2: "m\u00B2", sqm: "m\u00B2", m: "m", m3: "m\u00B3",
  kg: "kg", tonne: "Tonne", hr: "hr", day: "Day", week: "Week",
  nr: "Nr", lm: "lm", linear_m: "m", set: "Set", lot: "Lot", pair: "Pair",
};

const CATEGORY_LUCIDE_MAP: Record<string, string> = {
  groundworks: "Droplets",
  foundations: "Landmark",
  brickwork: "Building2",
  steel: "Columns3",
  carpentry: "Hammer",
  roofing: "Home",
  windows_doors: "DoorOpen",
  internal_walls: "Layers",
  plastering: "PaintBucket",
  insulation: "Grid3X3",
  electrics: "Zap",
  electrical: "Zap",
  plumbing: "Wrench",
  ventilation: "Fan",
  flooring: "Scaling",
  tiling: "Cuboid",
  decorating: "PaintBucket",
  kitchens: "Armchair",
  bathrooms: "Droplets",
  scaffolding: "ArrowUpFromLine",
  waste: "Trash2",
  making_good: "Package",
  preliminaries: "HardHat",
  flat_roofing: "Home",
  demolition: "Trash2",
  skip: "Trash2",
  stud_walls: "Layers",
  heating: "Flame",
  damp_proofing: "ShieldCheck",
  equipment_hire: "Truck",
  legal_fees: "Scale",
  site_equipment: "Construction",
  fences_barriers: "Fence",
  preparation_protection: "Shield",
  wall_formwork: "Landmark",
  formwork: "Landmark",
  legal: "Scale",
  plant: "Truck",
  hoarding: "Fence",
  protection: "Shield",
};

// Icon per summary sub-group (drawn as a teal pill badge in the Summary PDF,
// matching the per-item category badges).
const SUB_GROUP_LUCIDE_MAP: Record<string, string> = {
  demolition_works: "Trash2",
  ground_works: "Droplets",
  masonry_works: "BrickWall",
  structural_steel: "Columns3",
  thermal_insulation: "Grid3X3",
  pitched_roofing: "Home",
  flat_roofing: "Home",
  roof_windows: "DoorOpen",
  fascias_guttering: "Home",
  windows_doors: "DoorOpen",
  first_fix_electrics: "Zap",
  second_fix_electrics: "Zap",
  first_fix_plumbing: "Wrench",
  second_fix_plumbing: "Wrench",
  heating: "Flame",
  drylining_works: "Layers",
  second_fix_joinery: "Hammer",
  making_good: "Package",
  decorative_finishes: "PaintBucket",
};

const M = 14;
const CW = 182;
type ColLayout = {
  itemX: number; itemW: number;
  catX: number; catW: number;
  qtyRightX: number;
  unitCenterX: number;
  unitCostRightX: number;
  subtotalRightX: number;
};
// Layout matches the on-screen Scope of Works preview: a wide Item
// column that wraps the description over 2 lines, a roomy Category badge
// area, and clearly-spaced currency columns on the right.
// Spacing rule (verified by rendered PDF, 9pt body): the widest currency
// value we expect ("£12,500.00" / "A$12,500.00") is ~16-17mm wide, so the
// right-aligned anchors of Unit Cost and Subtotal must sit at least ~20mm
// apart to leave a visible gap between adjacent values; Unit (centred)
// must sit at least ~6mm clear of the Unit Cost value's left edge.
const COL_UK: ColLayout = {
  itemX: M + 6,
  itemW: 64,
  catX: M + 74,
  catW: 40,
  qtyRightX: M + 124,
  unitCenterX: M + 134,
  unitCostRightX: M + 156,
  subtotalRightX: M + CW - 6,
};
// AU layout: A$ prefix is wider than £, so currency columns need a touch
// more breathing room.
const COL_AU: ColLayout = {
  itemX: M + 6,
  itemW: 58,
  catX: M + 68,
  catW: 36,
  qtyRightX: M + 118,
  unitCenterX: M + 128,
  unitCostRightX: M + 154,
  subtotalRightX: M + CW - 6,
};
function getColumns(market?: string | null): ColLayout {
  // CA and US reuse the AU column layout (C$ / $ symbols are similar widths).
  return (market === "au" || market === "ca" || market === "us") ? COL_AU : COL_UK;
}

function formatCurrency(value: string | null | undefined, market?: string): string {
  const num = parseFloat(value || "0");
  if (market === "au") {
    return `A$${num.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (market === "ca") {
    return `C$${num.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (market === "us") {
    return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `\u00A3${num.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getCategoryLabel(value: string, market?: string | null): string {
  const cats = (market === "au" || market === "ca" || market === "us") ? getCostCategories(market) : COST_CATEGORIES;
  return cats.find(c => c.value === value)?.label || value;
}

function getUnitLabel(value: string): string {
  return UNIT_LABELS[value] || value;
}

export function getLogoDimensions(
  width: number,
  height: number,
  size: CompanyInfo["logoSize"] = "medium",
): { width: number; height: number } | null {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  const bounds = {
    small: { width: 45, height: 16 },
    medium: { width: 65, height: 24 },
    large: { width: 85, height: 32 },
  }[size || "medium"];
  const scale = Math.min(bounds.width / width, bounds.height / height);
  return { width: width * scale, height: height * scale };
}

export function loadImageAsBase64(url: string, removeBackground = false): Promise<{ data: string; width: number; height: number } | null> {
  return new Promise((resolve, reject) => {
    // Node-based export tests cannot load browser images. In the browser every
    // request settles even when storage is unavailable, so preview never hangs.
    if (typeof Image === "undefined") { resolve(null); return; }
    const img = new Image();
    const timer = setTimeout(() => { img.src = ""; resolve(null); }, 10_000);
    img.crossOrigin = "anonymous";
    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        if (removeBackground) removeLogoCanvasBackground(canvas, ctx);
        resolve({ data: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight });
      } catch (error) {
        if (removeBackground) { reject(error); return; }
        // Cross-origin and malformed images can fail while being drawn even
        // after onload. Treat them as unavailable so the name fallback renders.
        resolve(null);
      }
    };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = url;
  });
}

function iconNodeToSvgString(iconNode: [string, Record<string, string>][], size: number, color: string): string {
  const elements = iconNode.map(([tag, attrs]) => {
    const attrStr = Object.entries(attrs).map(([k, v]) => `${k}="${v}"`).join(" ");
    return `<${tag} ${attrStr}/>`;
  }).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${elements}</svg>`;
}

function svgStringToImage(svgString: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load SVG icon"));
    };
    const blob = new Blob([svgString], { type: "image/svg+xml" });
    img.src = URL.createObjectURL(blob);
  });
}


async function renderCategoryIconPng(category: string, color = "#65A2AE"): Promise<string | null> {
  const catKey = category.toLowerCase().replace(/[\s&-]+/g, "_").replace(/_+/g, "_");
  let lucideIconName = CATEGORY_LUCIDE_MAP[catKey];
  if (!lucideIconName) {
    for (const [mapKey, iconName] of Object.entries(CATEGORY_LUCIDE_MAP)) {
      if (catKey.includes(mapKey) || mapKey.includes(catKey)) {
        lucideIconName = iconName;
        break;
      }
    }
  }
  if (!lucideIconName || !ICON_NODES[lucideIconName]) return null;
  try {
    const iconNode = ICON_NODES[lucideIconName];
    const svgStr = iconNodeToSvgString(iconNode, 36, color);
    const img = await svgStringToImage(svgStr);
    const canvas = document.createElement("canvas");
    canvas.width = 36;
    canvas.height = 36;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, 36, 36);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

async function preRenderCategoryBadges(items: QuoteLineItem[], color?: string): Promise<Record<string, string>> {
  const uniqueCategories = Array.from(new Set(items.map(i => i.category)));
  const icons: Record<string, string> = {};
  for (const cat of uniqueCategories) {
    const dataUrl = await renderCategoryIconPng(cat, color);
    if (dataUrl) icons[cat] = dataUrl;
  }
  return icons;
}

// Render a single named lucide icon (HardHat, BrickWall, Wrench, ...) to a
// PNG data URL at the given hex colour. Used for the per-item Labour /
// Material / Plant breakdown chips so they match the on-screen view.
async function renderNamedIconPng(iconName: string, color: string = "#5aabbf"): Promise<string | null> {
  if (!ICON_NODES[iconName]) return null;
  try {
    const svgStr = iconNodeToSvgString(ICON_NODES[iconName], 36, color);
    const img = await svgStringToImage(svgStr);
    const canvas = document.createElement("canvas");
    canvas.width = 36;
    canvas.height = 36;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, 36, 36);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

async function preRenderBreakdownIcons(color?: string): Promise<{ labour: string | null; material: string | null; plant: string | null }> {
  const [labour, material, plant] = await Promise.all([
    renderNamedIconPng("HardHat", color),
    renderNamedIconPng("BrickWall", color),
    renderNamedIconPng("Construction", color),
  ]);
  return { labour, material, plant };
}

export async function generateQuotePDF(quote: QuoteData, company?: CompanyInfo, options?: { returnBase64?: boolean; onWarning?: (message: string) => void }): Promise<string | void> {
  const doc = new jsPDF();
  const snapshot = readPdfTemplateSnapshot(quote.pdfTemplate);
  // A quote-owned branding snapshot takes precedence over current account
  // settings. Changing a saved design must not change previously issued PDFs.
  if (snapshot) company = {
    market: company?.market,
    ...snapshot.company,
    // Absence in persisted JSON is authoritative too: do not inherit a new
    // logo, address, VAT number or other current-profile branding field.
    logoUrl: snapshot.design.logoUrl ?? undefined,
  };
  let template = snapshot?.design ?? null;
  await preparePdfTitleFont(doc, template);
  const [templateLogo, coverArt, closingArt] = template ? await Promise.all([
    template.logoUrl ? loadImageAsBase64(template.logoUrl, template.removeLogoBackground) : null,
    template.coverEnabled && template.coverArtworkUrl ? loadImageAsBase64(template.coverArtworkUrl) : null,
    template.thankYouEnabled && template.thankYouArtworkUrl ? loadImageAsBase64(template.thankYouArtworkUrl) : null,
  ]) : [null, null, null];
  if (template && ((template.logoUrl && !templateLogo) || (template.coverEnabled && template.coverArtworkUrl && !coverArt) ||
    (template.thankYouEnabled && template.thankYouArtworkUrl && !closingArt))) {
    const message = "Some PDF design images could not be loaded. The quote has been exported in its original layout.";
    options?.onWarning?.(message);
    if (!options?.onWarning && typeof window !== "undefined") window.dispatchEvent(new CustomEvent("pdf-template-warning", { detail: message }));
    if (template.logoUrl && !templateLogo && company) company = { ...company, logoUrl: undefined };
    template = null;
  }
  if (template?.coverEnabled) {
    drawTemplateCover(doc, quote, company, template, { logo: templateLogo, artwork: coverArt }, formatCurrency(quote.grandTotal, company?.market));
    doc.addPage();
  }
  const interiorColor = pdfInteriorColour(template);
  const iconColor = pdfIconColour(template);
  const accentRgb: [number, number, number] = template?.useInteriorBranding && template.textColor
    ? hexRgb(template.textColor) : interiorColor ? hexRgb(interiorColor) : [101, 162, 174];
  const badgeBgRgb: [number, number, number] = interiorColor ? tintRgb(interiorColor) : [232, 242, 244];
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  const bottomMargin = 22;
  let yPos = 15;

  const cardBgR = 243, cardBgG = 243, cardBgB = 245;
  const cardBorderR = 225, cardBorderG = 225, cardBorderB = 228;

  const COL = getColumns(company?.market);

  const allItems = quote.lineItems.map(withQuotedCostSplit);
  const coreItems = allItems.filter(item => !item.isOptional);
  const optionalItems = allItems.filter(item => !!item.isOptional);
  const [badgeImages, breakdownIcons] = await Promise.all([
    preRenderCategoryBadges(allItems, iconColor),
    preRenderBreakdownIcons(iconColor),
  ]);

  function drawCard(x: number, y: number, w: number, h: number) {
    doc.setFillColor(cardBgR, cardBgG, cardBgB);
    doc.setDrawColor(cardBorderR, cardBorderG, cardBorderB);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, w, h, 2.5, 2.5, "FD");
  }

  function checkPageBreak(yPos: number, neededHeight: number): number {
    if (yPos + neededHeight > pageHeight - bottomMargin) {
      doc.addPage();
      return 20;
    }
    return yPos;
  }

  let logoXEnd = margin;
  let actualLogoH = 0;
  let logoRendered = false;
  const logoTopY = yPos - 5;
  if (company?.logoUrl) {
    const logoResult = await loadImageAsBase64(company.logoUrl, template?.removeLogoBackground);
    const dimensions = logoResult
      ? getLogoDimensions(logoResult.width, logoResult.height, company.logoSize)
      : null;
    if (logoResult && dimensions) {
      try {
        doc.addImage(logoResult.data, "PNG", margin, logoTopY, dimensions.width, dimensions.height);
        logoXEnd = margin + dimensions.width + 6;
        actualLogoH = dimensions.height;
        logoRendered = true;
      } catch {
        // Invalid uploaded image data must not prevent quote export.
      }
    }
  }

  // A failed or missing logo always falls back to the company name. When a
  // valid logo is present, builders can hide a duplicate name embedded in it.
  let nameBottomY = logoTopY + actualLogoH;
  if (!logoRendered || company?.showCompanyName !== false) {
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    const contactColumnX = pageWidth - margin - 82;
    const besideLogoWidth = contactColumnX - logoXEnd;
    const placeBelowLogo = logoRendered && besideLogoWidth < 40;
    const nameX = placeBelowLogo ? margin : logoXEnd;
    const nameY = placeBelowLogo
      ? logoTopY + actualLogoH + 6
      : (actualLogoH > 0 ? logoTopY + actualLogoH / 2 + 3 : yPos);
    const availableNameWidth = Math.max(20, contactColumnX - nameX);
    const companyName = company?.name || "The Proper Tool";
    const fittedName = doc.getTextWidth(companyName) <= availableNameWidth
      ? companyName
      : `${companyName.slice(0, Math.max(1, Math.floor(companyName.length * availableNameWidth / doc.getTextWidth(companyName)) - 3))}...`;
    doc.text(fittedName, nameX, nameY);
    nameBottomY = nameY + 2;
  }

  let headerRightY = 15;
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(110, 110, 110);

  if (company?.address) {
    const addressLines = doc.splitTextToSize(company.address, 80);
    addressLines.forEach((line: string) => {
      doc.text(line, pageWidth - margin, headerRightY, { align: "right" });
      headerRightY += 3.8;
    });
  }
  if (company?.phone) {
    doc.text(company.phone, pageWidth - margin, headerRightY, { align: "right" });
    headerRightY += 3.8;
  }
  if (company?.email) {
    doc.text(company.email, pageWidth - margin, headerRightY, { align: "right" });
    headerRightY += 3.8;
  }
  if (company?.website) {
    doc.text(company.website, pageWidth - margin, headerRightY, { align: "right" });
    headerRightY += 3.8;
  }
  if (company?.vatNumber) {
    const isAU = company.market === "au";
    const isCA = company.market === "ca";
    const isUS = company.market === "us";
    const vatLabel = isAU ? "ABN" : isCA ? "GST/HST" : isUS ? "EIN / TIN" : "VAT";
    doc.text(`${vatLabel}: ${company.vatNumber}`, pageWidth - margin, headerRightY, { align: "right" });
    headerRightY += 3.8;
  }

  yPos = Math.max(logoTopY + actualLogoH + 6, nameBottomY + 4, yPos + 12, headerRightY + 4);

  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 7;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(40, 40, 40);
  doc.text(quote.quoteNumber, margin, yPos);

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(110, 110, 110);
  yPos += 8;

  const halfWidth = (contentWidth - 6) / 2;

  // showMarkupBreakdown defaults to true so existing PDFs are unchanged.
  // When explicitly false, Contingency / Overhead / Profit rows are omitted
  // and the summary jumps from Subtotal directly to Net Total.
  const showMarkupBreakdown = quote.showMarkupBreakdown !== false;

  const summaryRows: { label: string; value: string; bold?: boolean }[] = [
    { label: "Subtotal", value: formatCurrency(quote.subtotal, company?.market) },
  ];
  if (showMarkupBreakdown) {
    if (Number(quote.contingencyAmount) > 0) {
      summaryRows.push({ label: "Contingency", value: formatCurrency(quote.contingencyAmount, company?.market) });
    }
    summaryRows.push({ label: "Overhead", value: formatCurrency(quote.overheadAmount, company?.market) });
    summaryRows.push({ label: "Profit", value: formatCurrency(quote.profitAmount, company?.market) });
  }
  summaryRows.push({ label: "Net Total", value: formatCurrency(quote.totalPrice, company?.market) });
  if (quote.includeVat) {
    const vatLabel = company?.market === "au" ? "GST" : (company?.market === "ca" || company?.market === "us") ? "Sales Tax" : "VAT";
    const vatRateFallback = company?.market === "au" ? "10.00" : (company?.market === "ca" || company?.market === "us") ? "0.00" : "20.00";
    summaryRows.push({ label: `${vatLabel} (${quote.vatRate || vatRateFallback}%)`, value: formatCurrency(quote.vatAmount, company?.market) });
  }
  summaryRows.push({ label: "Grand Total", value: formatCurrency(quote.grandTotal, company?.market), bold: true });

  let simulatedSumH = 18;
  summaryRows.forEach((row) => {
    if (row.label === "Net Total") { simulatedSumH += 6; }
    if (row.bold) { simulatedSumH += 14; }
    else { simulatedSumH += 5.5; }
  });
  simulatedSumH += 4;
  const summaryContentH = simulatedSumH;
  // Two-column field layout: fields stack within their own column. Each
  // field reserves 16mm (label 4 + 2 lines of value 8 + 4 bottom pad), so
  // we measure the taller of the two columns and add the title strip.
  const leftFields = [quote.client, quote.projectType].filter(Boolean).length;
  const rightFields = [quote.projectAddress, quote.validUntil].filter(Boolean).length;
  const detailContentH = 14 + Math.max(leftFields, rightFields) * 16;
  const cardH = Math.max(summaryContentH, detailContentH, 44);

  drawCard(margin, yPos, halfWidth, cardH);

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(30, 30, 30);
  doc.text("Project Details", margin + 8, yPos + 10);

  // Two-column field layout inside the Project Details card. We measure
  // the project address with splitTextToSize so long values wrap onto a
  // second line instead of spilling outside the card border.
  const leftColX = margin + 8;
  const rightColX = margin + halfWidth / 2 + 4;
  // Each column gets roughly half the card width minus inner padding.
  const colW = halfWidth / 2 - 12;

  const drawDetailField = (x: number, yRef: { y: number }, label: string, value: string, maxW: number) => {
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(140, 140, 140);
    doc.text(label, x, yRef.y);
    yRef.y += 4;
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40, 40, 40);
    const lines: string[] = doc.splitTextToSize(value, maxW);
    // Cap at 2 lines to keep card height predictable.
    const shownLines = lines.slice(0, 2);
    if (lines.length > 2) {
      const last = shownLines[shownLines.length - 1];
      shownLines[shownLines.length - 1] = last.replace(/\s*\S*$/, "") + "...";
    }
    shownLines.forEach((ln) => {
      doc.text(ln, x, yRef.y);
      yRef.y += 4;
    });
    doc.setFont("helvetica", "normal");
    yRef.y += 4;
  };

  const leftRef = { y: yPos + 17 };
  const rightRef = { y: yPos + 17 };

  if (quote.client) {
    drawDetailField(leftColX, leftRef, "Client", quote.client.name, colW);
  }
  if (quote.projectType) {
    drawDetailField(leftColX, leftRef, "Project Type", quote.projectType.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()), colW);
  }
  if (quote.projectAddress) {
    drawDetailField(rightColX, rightRef, "Project Address", quote.projectAddress, colW);
  }
  if (quote.validUntil) {
    const dateFmt = company?.market === "us" ? "MM/dd/yyyy" : "dd/MM/yyyy";
    drawDetailField(rightColX, rightRef, "Valid Until", format(new Date(quote.validUntil), dateFmt), colW);
  }

  const summaryX = margin + halfWidth + 6;
  drawCard(summaryX, yPos, halfWidth, cardH);

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(30, 30, 30);
  doc.text("Quote Summary", summaryX + 8, yPos + 10);

  let sumY = yPos + 18;
  const sumLabelX = summaryX + 8;
  const sumValueX = summaryX + halfWidth - 8;

  summaryRows.forEach((row) => {
    if (row.label === "Net Total") {
      sumY += 1;
      doc.setDrawColor(210, 210, 210);
      doc.setLineWidth(0.3);
      doc.line(sumLabelX, sumY, sumValueX, sumY);
      sumY += 5;
    }
    if (row.bold) {
      sumY += 1;
      doc.setDrawColor(210, 210, 210);
      doc.setLineWidth(0.3);
      doc.line(sumLabelX, sumY, sumValueX, sumY);
      sumY += 6;
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text(row.label, sumLabelX, sumY);
      doc.setTextColor(...accentRgb);
      doc.text(row.value, sumValueX, sumY, { align: "right" });
      sumY += 7;
    } else {
      doc.setFontSize(8.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(110, 110, 110);
      doc.text(row.label, sumLabelX, sumY);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 40, 40);
      doc.text(row.value, sumValueX, sumY, { align: "right" });
      sumY += 5.5;
    }
  });

  yPos += cardH + 10;

  // Customer-facing visibility toggles. Default to "show" when omitted so
  // existing PDFs render exactly the same as before.
  // "Total Price Only" (showLineItems === false) keeps the item list and
  // categories visible but hides all per-item pricing (Qty/Unit/Unit Cost/
  // Subtotal and the cost breakdown) — only the totals summary shows a price.
  const totalPriceOnly = quote.showLineItems === false;
  const showDescriptions = quote.showItemDescriptions !== false;
  const showQtyUnits = quote.showQuantityUnits !== false && !totalPriceOnly;

  function drawTableHeader(y: number): number {
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.2);
    doc.line(margin + 4, y + 10, margin + contentWidth - 4, y + 10);

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(130, 130, 130);

    doc.text("Item", COL.itemX, y + 6);
    doc.text("Category", COL.catX, y + 6);
    if (showQtyUnits) {
      doc.text("Qty", COL.qtyRightX, y + 6, { align: "right" });
      doc.text("Unit", COL.unitCenterX, y + 6, { align: "center" });
      doc.text("Unit Cost", COL.unitCostRightX, y + 6, { align: "right" });
    }
    if (!totalPriceOnly) {
      doc.text("Subtotal", COL.subtotalRightX, y + 6, { align: "right" });
    }

    return y + 13;
  }

  // Should the per-item Labour / Material / Plant breakdown line appear under
  // the description? Only when the quote has the customer-facing toggle ON
  // AND the item actually has a non-zero split to show.
  function shouldShowItemBreakdown(item: QuoteLineItem): boolean {
    if (!quote.showCostBreakdown || totalPriceOnly) return false;
    const lab = parseFloat(String(item.labourCost ?? 0)) || 0;
    const mat = parseFloat(String(item.materialCost ?? 0)) || 0;
    const plt = parseFloat(String(item.plantCost ?? 0)) || 0;
    return lab > 0 || mat > 0 || plt > 0;
  }

  function measureRowHeight(item: QuoteLineItem): number {
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    const nameLines = doc.splitTextToSize(item.name.replace(/\b\w/g, l => l.toUpperCase()), COL.itemW);
    let h = nameLines.length * 4.2;

    const displayDescription = item.customerDescription || item.description;
    if (displayDescription && showDescriptions) {
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "normal");
      const descLines = doc.splitTextToSize(displayDescription, COL.itemW);
      h += descLines.length * 3.6 + 1.5;
    }

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    const categoryText = quoteCategoryLabel(quote.headingNames, item, getCategoryLabel(item.category, company?.market));
    const categoryLines = doc.splitTextToSize(categoryText, COL.catW - 10);
    h = Math.max(h, categoryLines.length * 3.6);
    // Top + bottom row padding for breathing room.
    let total = h + 10;

    if (shouldShowItemBreakdown(item)) {
      // Breakdown sits on its own sub-row, right-aligned under the
      // Subtotal column (mirrors the on-screen preview). Adds ~6mm.
      total += 6;
    }

    return Math.max(total, 18);
  }

  function drawItemRow(item: QuoteLineItem, y: number): number {
    const rowH = measureRowHeight(item);
    const hasBreakdown = shouldShowItemBreakdown(item);
    // Vertical centre of the "primary" content area (excluding the
    // breakdown sub-row at the bottom, if any). Used for category badge
    // and currency columns so they line up with the item name.
    const primaryH = hasBreakdown ? rowH - 6 : rowH;

    doc.setDrawColor(225, 225, 225);
    doc.setLineWidth(0.15);
    doc.line(margin + 4, y + rowH, margin + contentWidth - 4, y + rowH);

    // --- Item name + description ---
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    const nameText = item.name.replace(/\b\w/g, l => l.toUpperCase());
    const nameLines = doc.splitTextToSize(nameText, COL.itemW);
    let textY = y + 5;
    nameLines.forEach((line: string) => {
      doc.text(line, COL.itemX, textY);
      textY += 4.2;
    });

    const displayDescription = item.customerDescription || item.description;
    if (displayDescription && showDescriptions) {
      textY += 0.8;
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(115, 115, 115);
      const descLines = doc.splitTextToSize(displayDescription, COL.itemW);
      descLines.forEach((line: string) => {
        doc.text(line, COL.itemX, textY);
        textY += 3.6;
      });
    }

    // --- Category badge (vertically centred against primary area) ---
    {
      const badgeLabel = quoteCategoryLabel(quote.headingNames, item,
        getCategoryLabel(item.category, company?.market).replace(/\b\w/g, l => l.toUpperCase()));
      const iconDataUrl = badgeImages[item.category];
      const padX = 2.4;
      const padY = 1.3;
      const iconMm = 3.6;
      const iconGap = 1.2;
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "normal");
      const textW = doc.getTextWidth(badgeLabel);
      const hasIcon = !!iconDataUrl;
      const badgeLines = doc.splitTextToSize(badgeLabel, COL.catW - padX * 2 - (hasIcon ? iconMm + iconGap : 0));
      const badgeW = Math.min(padX + (hasIcon ? iconMm + iconGap : 0) + textW + padX, COL.catW);
      const badgeH = Math.max(iconMm, badgeLines.length * 3.3) + padY * 2;
      const badgeY = y + (primaryH - badgeH) / 2;
      doc.setFillColor(...badgeBgRgb);
      doc.roundedRect(COL.catX, badgeY, badgeW, badgeH, badgeH / 2, badgeH / 2, "F");
      let labelX = COL.catX + padX;
      if (hasIcon) {
        try {
          doc.addImage(iconDataUrl, "PNG", labelX, badgeY + padY, iconMm, iconMm);
        } catch { }
        labelX += iconMm + iconGap;
      }
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(58, 58, 58);
      doc.text(badgeLines, labelX, badgeY + padY + 2.5);
    }

    // --- Qty / Unit / Unit Cost / Subtotal (centred in primary area) ---
    const dataY = y + primaryH / 2 + 1;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(40, 40, 40);
    if (showQtyUnits) {
      doc.text(parseFloat(item.quantity).toFixed(2), COL.qtyRightX, dataY, { align: "right" });
      doc.text(getUnitLabel(item.unit), COL.unitCenterX, dataY, { align: "center" });
      doc.text(formatCurrency(item.unitCost, company?.market), COL.unitCostRightX, dataY, { align: "right" });
    }

    if (!totalPriceOnly) {
      doc.setFont("helvetica", "bold");
      doc.text(formatCurrency(item.subtotal, company?.market), COL.subtotalRightX, dataY, { align: "right" });
    }

    // --- Breakdown sub-row with icons (mirrors on-screen preview) ---
    // Layout: [icon] Labour £X  ·  [icon] Material £Y  ·  [icon] Plant £Z
    // Right-anchored under the Subtotal column.
    if (hasBreakdown) {
      const breakdownY = y + primaryH + 4;
      const fontSize = 7;
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", "normal");
      const labelColor: [number, number, number] = [110, 130, 135];
      const valueColor: [number, number, number] = [60, 70, 75];
      const sepColor: [number, number, number] = [180, 190, 195];
      const lab = formatCurrency(String(item.labourCost ?? 0), company?.market);
      const mat = formatCurrency(String(item.materialCost ?? 0), company?.market);
      const plt = formatCurrency(String(item.plantCost ?? 0), company?.market);

      type Chip = { icon: string | null; label: string; value: string };
      const chips: Chip[] = [
        { icon: breakdownIcons.labour, label: "Labour", value: lab },
        { icon: breakdownIcons.material, label: "Material", value: mat },
        { icon: breakdownIcons.plant, label: "Plant", value: plt },
      ];
      const iconMm = 2.6;
      const iconGap = 1.0;
      const labelGap = 1.2;
      const sepGap = 2.0;
      const sepChar = "\u00B7";

      // Measure total width so we can right-anchor the whole composition.
      let totalW = 0;
      chips.forEach((c, i) => {
        if (c.icon) totalW += iconMm + iconGap;
        totalW += doc.getTextWidth(c.label) + labelGap + doc.getTextWidth(c.value);
        if (i < chips.length - 1) {
          totalW += sepGap + doc.getTextWidth(sepChar) + sepGap;
        }
      });

      let x = COL.subtotalRightX - totalW;
      const iconY = breakdownY - iconMm + 0.4; // visually centre on text baseline
      chips.forEach((c, i) => {
        if (c.icon) {
          try { doc.addImage(c.icon, "PNG", x, iconY, iconMm, iconMm); } catch { }
          x += iconMm + iconGap;
        }
        doc.setTextColor(...labelColor);
        doc.text(c.label, x, breakdownY);
        x += doc.getTextWidth(c.label) + labelGap;
        doc.setTextColor(...valueColor);
        doc.text(c.value, x, breakdownY);
        x += doc.getTextWidth(c.value);
        if (i < chips.length - 1) {
          x += sepGap;
          doc.setTextColor(...sepColor);
          doc.text(sepChar, x, breakdownY);
          x += doc.getTextWidth(sepChar) + sepGap;
        }
      });
    }

    return y + rowH;
  }

  function renderItemsSection(items: QuoteLineItem[], sectionTitle: string, subtitle: string) {
    const cardPad = 8;
    const headerAreaH = 18;
    const tableHeaderH = 13;

    yPos = checkPageBreak(yPos, 50);

    let totalRowsH = 0;
    items.forEach(item => { totalRowsH += measureRowHeight(item); });
    const totalSectionH = cardPad + headerAreaH + tableHeaderH + totalRowsH + cardPad;

    const availableOnPage = pageHeight - bottomMargin - yPos;
    const fitsOnOnePage = totalSectionH <= availableOnPage;

    if (fitsOnOnePage) {
      drawCard(margin, yPos, contentWidth, totalSectionH);

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text(sectionTitle, margin + cardPad, yPos + cardPad + 4);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(130, 130, 130);
      doc.text(subtitle, margin + cardPad, yPos + cardPad + 9);
      yPos += cardPad + headerAreaH;

      yPos = drawTableHeader(yPos);

      for (const item of items) {
        yPos = drawItemRow(item, yPos);
      }

      yPos += cardPad + 8;
    } else {
      const contHeaderH = cardPad + 14;

      interface PageSegment {
        startItemIdx: number;
        endItemIdx: number;
        isContinued: boolean;
      }

      const segments: PageSegment[] = [];
      let simY = yPos + cardPad + headerAreaH + tableHeaderH;
      let segStartIdx = 0;

      for (let i = 0; i < items.length; i++) {
        const rowH = measureRowHeight(items[i]);
        if (simY + rowH > pageHeight - bottomMargin && i > segStartIdx) {
          segments.push({ startItemIdx: segStartIdx, endItemIdx: i, isContinued: segments.length > 0 });
          segStartIdx = i;
          simY = 20 + contHeaderH + tableHeaderH;
        }
        simY += rowH;
      }
      segments.push({ startItemIdx: segStartIdx, endItemIdx: items.length, isContinued: segments.length > 0 });

      for (let s = 0; s < segments.length; s++) {
        const seg = segments[s];
        if (s > 0) {
          doc.addPage();
          yPos = 20;
        }

        let segRowsH = 0;
        for (let i = seg.startItemIdx; i < seg.endItemIdx; i++) {
          segRowsH += measureRowHeight(items[i]);
        }

        const segTitleH = seg.isContinued ? contHeaderH : (cardPad + headerAreaH);
        const segH = segTitleH + tableHeaderH + segRowsH + cardPad;

        drawCard(margin, yPos, contentWidth, segH);

        if (seg.isContinued) {
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(30, 30, 30);
          doc.text(`${sectionTitle} (continued)`, margin + cardPad, yPos + cardPad + 4);
          yPos += contHeaderH;
        } else {
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(30, 30, 30);
          doc.text(sectionTitle, margin + cardPad, yPos + cardPad + 4);
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(130, 130, 130);
          doc.text(subtitle, margin + cardPad, yPos + cardPad + 9);
          yPos += cardPad + headerAreaH;
        }

        yPos = drawTableHeader(yPos);

        for (let i = seg.startItemIdx; i < seg.endItemIdx; i++) {
          yPos = drawItemRow(items[i], yPos);
        }

        yPos += cardPad;
      }

      yPos += 8;
    }
  }

  // Summary mode renderer: one card with category rows and optional sub-group rows.
  function renderSummarySection(
    rows: { category: string; total: number; subGroups: { label: string; total: number; icon?: string | null }[] }[],
  ): void {
    if (rows.length === 0) return;
    const cPad = 5;
    const ROW_H = 11;
    const SUB_H = 9;
    const rightX = margin + contentWidth - 6;

    const dimensions = rows.map(row => {
      doc.setFontSize(9.5);
      doc.setFont("helvetica", "bold");
      const title = doc.splitTextToSize(quoteHeadingLabel(quote.headingNames, row.category), contentWidth - 48);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      const subgroups = row.subGroups.map(sg =>
        doc.splitTextToSize(quoteHeadingLabel(quote.headingNames, sg.label, "subgroup"), contentWidth - 65));
      return { title, height: Math.max(ROW_H, title.length * 4 + 5), subgroups };
    });
    let totalH = cPad + 18 + 4 + cPad;
    dimensions.forEach(r => { totalH += r.height + r.subgroups.reduce((sum, lines) => sum + Math.max(SUB_H, lines.length * 3.5 + 5), 0) + 3; });

    yPos = checkPageBreak(yPos, Math.min(totalH, pageHeight - bottomMargin - 20));
    drawCard(margin, yPos, contentWidth, totalH);

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text("Scope of Works", margin + cPad, yPos + cPad + 5);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(130, 130, 130);
    doc.text("Summary of work categories in this quote.", margin + cPad, yPos + cPad + 10);
    yPos += cPad + 18;

    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.2);
    doc.line(margin + 4, yPos, margin + contentWidth - 4, yPos);
    yPos += 4;

    rows.forEach((row, i) => {
      doc.setFontSize(9.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text(dimensions[i].title, margin + 6, yPos + 6);
      if (shouldShowPdfSummaryPrices(quote)) {
        doc.setTextColor(...accentRgb);
        doc.text(formatCurrency(String(row.total), company?.market), rightX, yPos + 6, { align: "right" });
      }
      yPos += dimensions[i].height;

      row.subGroups.forEach((sg, sgIndex) => {
        // Teal pill badge with icon + label, same styling as the category
        // badges in the detailed Scope of Works / Optional Works tables.
        const padX = 2.4;
        const padY = 1.3;
        const iconMm = 3.6;
        const iconGap = 1.2;
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        const textLines = dimensions[i].subgroups[sgIndex];
        const textW = Math.max(...textLines.map((line: string) => doc.getTextWidth(line)));
        const hasIcon = !!sg.icon;
        const badgeW = padX + (hasIcon ? iconMm + iconGap : 0) + textW + padX;
        const badgeH = Math.max(iconMm, textLines.length * 3.5) + padY * 2;
        const subHeight = Math.max(SUB_H, badgeH + 2);
        const badgeY = yPos + (subHeight - badgeH) / 2;
        doc.setFillColor(...badgeBgRgb);
        doc.roundedRect(margin + 12, badgeY, badgeW, badgeH, badgeH / 2, badgeH / 2, "F");
        let labelX = margin + 12 + padX;
        if (hasIcon) {
          try {
            doc.addImage(sg.icon!, "PNG", labelX, badgeY + padY, iconMm, iconMm);
          } catch { }
          labelX += iconMm + iconGap;
        }
        doc.setTextColor(58, 58, 58);
        doc.text(textLines, labelX, badgeY + padY + 2.5);
        if (shouldShowPdfSummaryPrices(quote)) {
          doc.setTextColor(60, 60, 60);
          doc.setFontSize(8.5);
          doc.text(formatCurrency(String(sg.total), company?.market), rightX, badgeY + badgeH / 2 + 0.4, { align: "right" });
        }
        yPos += subHeight;
      });

      if (i < rows.length - 1) {
        doc.setDrawColor(225, 225, 225);
        doc.setLineWidth(0.15);
        doc.line(margin + 6, yPos + 1, margin + contentWidth - 6, yPos + 1);
        yPos += 3;
      }
    });

    yPos += cPad + 8;
  }

  const sortByPriority = (a: QuoteLineItem, b: QuoteLineItem) => {
    const stageOrder = ["Preparation", "Demolition", "Substructure", "Structure", "Roofing", "External Works", "Finishes", "Services", "Other"];
    const rank = (item: QuoteLineItem) => {
      const index = stageOrder.indexOf(getEffectiveStage(item));
      return index < 0 ? stageOrder.length : index;
    };
    const prioA = rank(a);
    const prioB = rank(b);
    if (prioA !== prioB) return prioA - prioB;
    return (a.sortOrder || 0) - (b.sortOrder || 0);
  };

  // Merge equivalent work items across scopes for the Combined multi-scope PDF.
  // Mirrors the on-screen Combined view merge: quantities and costs are summed,
  // bundled "item"-unit rows collapse to qty 1 with the summed total as the
  // unit cost, and the merged row takes the cleanest work-type name.
  const mergeEquivalentPdfItems = (items: QuoteLineItem[]): QuoteLineItem[] => {
    const groups = groupEquivalentLineItems(
      items.map(item => ({
        name: item.name || "",
        category: item.category || "",
        unit: item.unit || "",
        unitCost: parseFloat(item.unitCost || "0"),
      })),
    );
    return groups.map(({ indices, displayName }) => {
      const members = indices.map(i => items[i]);
      const first = members[0];
      if (members.length === 1) return { ...first, name: displayName };
      const mergedTotal = members.reduce((s, m) => s + parseFloat(m.subtotal || "0"), 0);
      const isBundled = (first.unit || "").toLowerCase() === "item";
      const mergedQty = isBundled ? 1 : members.reduce((s, m) => s + parseFloat(m.quantity || "0"), 0);
      const sumField = (f: (m: QuoteLineItem) => string | null | undefined): string | null => {
        const vals = members.map(m => f(m)).filter((v): v is string => v != null && v !== "");
        if (vals.length === 0) return null;
        return String(vals.reduce((s, v) => s + parseFloat(v || "0"), 0));
      };
      return {
        ...first,
        name: displayName,
        quantity: String(mergedQty),
        unitCost: String(isBundled ? mergedTotal : (mergedQty > 0 ? mergedTotal / mergedQty : parseFloat(first.unitCost || "0"))),
        subtotal: String(mergedTotal),
        labourCost: sumField(m => m.labourCost),
        materialCost: sumField(m => m.materialCost),
        plantCost: sumField(m => m.plantCost),
      };
    });
  };

  if (quote.summaryMode && coreItems.length > 0) {
    // Summary mode: one row per category + sub-group, no individual items.
    const itemsForSummary = (quote.isMultiScope && Array.isArray(quote.scopeNames) && quote.scopeNames.length > 0)
      ? mergeEquivalentPdfItems(coreItems).sort(sortByPriority)
      : [...coreItems].sort(sortByPriority);
    const catTotals = new Map<string, number>();
    const catSubGroupMaps = new Map<string, Map<string, number>>();
    const subGroupOfLabel = new Map<string, ItemSubGroupId>();
    for (const item of itemsForSummary) {
      const sg = getEffectiveSubGroup(item);
      // Sub-groups have one canonical section — without this, the same
      // sub-group label (e.g. "Demolition Works") shows under two sections.
      const section = item.scopeName === "__shared__"
        ? "Shared Costs"
        : (item.displayStage?.trim() || (sg ? SUB_GROUP_SECTION[sg as ItemSubGroupId] : getCategorySection(item.category)));
      catTotals.set(section, (catTotals.get(section) ?? 0) + parseFloat(item.subtotal || "0"));
      if (sg) {
        const sgLabel = item.displaySubGroup?.trim() || (ITEM_SUB_GROUP_LABELS[sg as ItemSubGroupId] ?? sg);
        subGroupOfLabel.set(sgLabel, sg as ItemSubGroupId);
        if (!catSubGroupMaps.has(section)) catSubGroupMaps.set(section, new Map());
        const sgMap = catSubGroupMaps.get(section)!;
        sgMap.set(sgLabel, (sgMap.get(sgLabel) ?? 0) + parseFloat(item.subtotal || "0"));
      }
    }
    // Pre-render an icon per sub-group label (same teal style as the category badges).
    const subGroupIcons: Record<string, string | null> = {};
    for (const [label, sgId] of Array.from(subGroupOfLabel.entries())) {
      const iconName = SUB_GROUP_LUCIDE_MAP[sgId];
      subGroupIcons[label] = iconName ? await renderNamedIconPng(iconName, iconColor || "#65A2AE") : null;
    }
    const sectionOrder = ["Shared Costs", ...QUOTE_PLACEMENT_STAGE_ORDER, ...Array.from(catTotals.keys()).filter(sec => !QUOTE_PLACEMENT_STAGE_ORDER.includes(sec as any) && sec !== "Shared Costs")];
    const summaryRows = sectionOrder.filter(sec => catTotals.has(sec)).map(sec => ({
      category: sec as string,
      total: catTotals.get(sec) ?? 0,
      subGroups: Array.from(catSubGroupMaps.get(sec)?.entries() ?? []).map(([label, total]) => ({ label, total, icon: subGroupIcons[label] ?? null })),
    }));
    if (summaryRows.length > 0) renderSummarySection(summaryRows);
  } else if (quote.isMultiScope && Array.isArray(quote.scopeNames) && quote.scopeNames.length > 0) {
    // Multi-scope combined PDF: merge equivalent work items across scopes.
    const mergedItems = mergeEquivalentPdfItems(coreItems).sort(sortByPriority);
    if (mergedItems.length > 0) {
      renderItemsSection(mergedItems, "Scope of Works", "Detailed breakdown of all items in this quote.");
    }
  } else {
    const sortedCoreItems = [...coreItems].sort(sortByPriority);
    if (sortedCoreItems.length > 0) {
      renderItemsSection(sortedCoreItems, "Scope of Works", "Detailed breakdown of all items in this quote.");
    }
  }

  // Optional Works section — rendered after all main scope sections, excluded from quoted price.
  if (optionalItems.length > 0) {
    const sortedOptionalItems = [...optionalItems].sort(sortByPriority);
    renderItemsSection(sortedOptionalItems, "Optional Works", "These items are not included in the quoted price.");
    // Draw a note below the optional section explaining the exclusion.
    const optionalSubtotal = sortedOptionalItems.reduce((s, it) => s + parseFloat(it.subtotal || "0"), 0);
    const noteH = 14;
    yPos = checkPageBreak(yPos, noteH);
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(150, 110, 30);
    doc.text(
      `Optional works total: ${formatCurrency(String(optionalSubtotal), company?.market)} — not included in the quoted price above.`,
      margin + 4, yPos
    );
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    yPos += noteH;
  }

  // Internal price-review notes — flagged outliers worth re-checking before sending.
  // Only renders when the quote has reviewerNotes starting with "Price flag".
  const reviewerNotesRaw = (quote as any).reviewerNotes;
  const priceFlagNotes: string[] = Array.isArray(reviewerNotesRaw)
    ? reviewerNotesRaw.filter((n: unknown): n is string => typeof n === "string" && n.startsWith("Price flag"))
    : [];
  if (priceFlagNotes.length > 0) {
    const lineHeight = 5;
    const cardPad = 8;
    const headerH = 12;
    const noteLines = priceFlagNotes.flatMap(n =>
      doc.splitTextToSize(n.replace(/^Price flag:\s*/, "• "), contentWidth - cardPad * 2 - 4)
    );
    const cardH = headerH + noteLines.length * lineHeight + cardPad;
    yPos = checkPageBreak(yPos, cardH + 6);
    doc.setFillColor(255, 247, 230);
    doc.setDrawColor(245, 158, 11);
    doc.roundedRect(margin, yPos, contentWidth, cardH, 2, 2, "FD");
    doc.setTextColor(120, 53, 15);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Review prices before sending", margin + cardPad, yPos + 8);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    let noteY = yPos + 14;
    for (const line of noteLines) {
      doc.text(line, margin + cardPad, noteY);
      noteY += lineHeight;
    }
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
    yPos += cardH + 8;
  }

  const hasAssumptions = quote.assumptions && quote.assumptions.trim();
  const hasExclusions = quote.exclusions && quote.exclusions.trim();

  if (hasAssumptions || hasExclusions) {
    yPos = checkPageBreak(yPos, 40);

    if (hasAssumptions && hasExclusions) {
      const assCardX = margin;
      const exclCardX = margin + halfWidth + 6;

      const assumptions = quote.assumptions!.split("\n").filter(a => a.trim());
      const exclusions = quote.exclusions!.split("\n").filter(e => e.trim());

      let assH = 18;
      assumptions.forEach(a => {
        const lines = doc.splitTextToSize(`\u2022  ${a}`, halfWidth - 18);
        assH += lines.length * 3.8 + 2;
      });

      let exclH = 18;
      exclusions.forEach(e => {
        const lines = doc.splitTextToSize(`\u2022  ${e}`, halfWidth - 18);
        exclH += lines.length * 3.8 + 2;
      });

      const sectionCardH = Math.max(assH, exclH) + 6;

      yPos = checkPageBreak(yPos, sectionCardH);
      drawCard(assCardX, yPos, halfWidth, sectionCardH);
      drawCard(exclCardX, yPos, halfWidth, sectionCardH);

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text("Assumptions", assCardX + 8, yPos + 10);
      doc.text("Exclusions", exclCardX + 8, yPos + 10);

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);

      let leftY = yPos + 17;
      assumptions.forEach(assumption => {
        const lines = doc.splitTextToSize(`\u2022  ${assumption}`, halfWidth - 18);
        lines.forEach((line: string) => {
          doc.text(line, assCardX + 8, leftY);
          leftY += 3.8;
        });
        leftY += 2;
      });

      let rightY = yPos + 17;
      exclusions.forEach(exclusion => {
        const lines = doc.splitTextToSize(`\u2022  ${exclusion}`, halfWidth - 18);
        lines.forEach((line: string) => {
          doc.text(line, exclCardX + 8, rightY);
          rightY += 3.8;
        });
        rightY += 2;
      });

      yPos += sectionCardH + 8;
    } else if (hasAssumptions) {
      const assumptions = quote.assumptions!.split("\n").filter(a => a.trim());
      let assH = 18;
      assumptions.forEach(a => {
        const lines = doc.splitTextToSize(`\u2022  ${a}`, contentWidth - 18);
        assH += lines.length * 3.8 + 2;
      });
      assH += 6;

      yPos = checkPageBreak(yPos, assH);
      drawCard(margin, yPos, contentWidth, assH);

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text("Assumptions", margin + 8, yPos + 10);

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      let leftY = yPos + 17;
      assumptions.forEach(assumption => {
        const lines = doc.splitTextToSize(`\u2022  ${assumption}`, contentWidth - 18);
        lines.forEach((line: string) => {
          doc.text(line, margin + 8, leftY);
          leftY += 3.8;
        });
        leftY += 2;
      });

      yPos += assH + 8;
    } else if (hasExclusions) {
      const exclusions = quote.exclusions!.split("\n").filter(e => e.trim());
      let exclH = 18;
      exclusions.forEach(e => {
        const lines = doc.splitTextToSize(`\u2022  ${e}`, contentWidth - 18);
        exclH += lines.length * 3.8 + 2;
      });
      exclH += 6;

      yPos = checkPageBreak(yPos, exclH);
      drawCard(margin, yPos, contentWidth, exclH);

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text("Exclusions", margin + 8, yPos + 10);

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      let rightY = yPos + 17;
      exclusions.forEach(exclusion => {
        const lines = doc.splitTextToSize(`\u2022  ${exclusion}`, contentWidth - 18);
        lines.forEach((line: string) => {
          doc.text(line, margin + 8, rightY);
          rightY += 3.8;
        });
        rightY += 2;
      });

      yPos += exclH + 8;
    }
  }

  if (quote.paymentTerms) {
    const paymentLines = doc.splitTextToSize(quote.paymentTerms, contentWidth - 18);
    const payCardH = 18 + paymentLines.length * 4 + 6;
    yPos = checkPageBreak(yPos, payCardH);

    drawCard(margin, yPos, contentWidth, payCardH);

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text("Payment Terms", margin + 8, yPos + 10);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(paymentLines, margin + 8, yPos + 17);

    yPos += payCardH + 8;
  }

  if (quote.termsConditions) {
    doc.addPage();
    yPos = 20;

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text("Terms & Conditions", margin, yPos);
    yPos += 8;

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);

    const termsLines = quote.termsConditions.split("\n");
    termsLines.forEach(line => {
      if (line.trim()) {
        yPos = checkPageBreak(yPos, 10);
        if (/^\d+\./.test(line.trim())) {
          doc.setFont("helvetica", "bold");
          doc.setTextColor(30, 30, 30);
        } else {
          doc.setFont("helvetica", "normal");
          doc.setTextColor(80, 80, 80);
        }
        const wrappedLines = doc.splitTextToSize(line, contentWidth);
        doc.text(wrappedLines, margin, yPos);
        yPos += wrappedLines.length * 3.5 + 1;
      } else {
        yPos += 3;
      }
    });
  }

  if (template?.thankYouEnabled) {
    doc.addPage();
    drawTemplateClosing(doc, company, template, { logo: templateLogo, artwork: closingArt });
  }
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    if ((template?.pageVersion === 2 || template?.pageVersion === 3 || template?.pageVersion === 4 || template?.pageVersion === 5 || template?.pageVersion === 6) && ((template.coverEnabled && i === 1) || (template.thankYouEnabled && i === pageCount))) {
      doc.saveGraphicsState();
      if (template.pageVersion === 5) doc.setGState(doc.GState({ opacity: 0.9 }));
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(pageWidth / 2 - 22, pageHeight - 13, 44, 9, (template.pageVersion ?? 0) >= 5 ? 3 : 1, (template.pageVersion ?? 0) >= 5 ? 3 : 1, "F");
      doc.restoreGraphicsState();
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
    }
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );
  }

  if (options?.returnBase64) {
    const dataUri = doc.output("datauristring");
    const commaIdx = dataUri.indexOf(",");
    return commaIdx >= 0 ? dataUri.slice(commaIdx + 1) : dataUri;
  }

  const scopeSuffix = quote.scopeLabel ? `-${quote.scopeLabel}` : "";
  doc.save(`${quote.quoteNumber}${scopeSuffix}.pdf`);
}
